import i18next from "i18next";
import { z } from "zod";
import {
  FIELD_CAMPAIGN_MUST_HAVE_ONE_DAY_ACTIVE,
  FIELD_REQUIRED,
  FIELD_NUMBER_NEGATIVE,
  START_DATE_AND_END_DATE_VALIDATION,
  FIELD_END_DATE_LESS_THAN_NOW,
} from "../../../../../i18n/keysTranslations";
import {
  ADS_CAMPAIGN_OBJECTIVE_WEBSITE_CONVERSIONS_OPTION,
  ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION,
} from "../../../../../utils/constants";
import { differenceInDays } from "date-fns";
import isAfter from "date-fns/isAfter";

const linkedinBaseSchema = z.object({
  objective: z.string().optional(),
  integrationID: z.string().min(1, i18next.t(FIELD_REQUIRED)),
  adAccountID: z.string().min(1, i18next.t(FIELD_REQUIRED)),
  typeBudget: z.string().min(1, i18next.t(FIELD_REQUIRED)),
  budget: z.any(),
  adFormat: z.string().min(1, i18next.t(FIELD_REQUIRED)),
  startDate: z.any().optional(),
  endDate: z.any().optional(),
  targetCPA: z.any().optional(),
  conversionActions: z.array(z.any()).optional(),
  customAudiences: z.array(z.any()).optional(),
  excludedCustomAudiences: z.array(z.any()).optional(),
  // Targeting fields moved to root level
  locations: z.array(z.any()).optional(),
  languages: z.any().optional(),
  ageRange: z.array(z.number()).optional(),
  genders: z.any().optional(),
  // Optional segmentations
  industries: z.array(z.any()).optional(),
  jobFunctions: z.array(z.any()).optional(),
  seniorities: z.array(z.any()).optional(),
  jobTitles: z.array(z.any()).optional(),
  companySizes: z.array(z.any()).optional(),
  companies: z.array(z.any()).optional(),
  degrees: z.array(z.any()).optional(),
  fieldsOfStudy: z.array(z.any()).optional(),
  schools: z.array(z.any()).optional(),
  skills: z.array(z.any()).optional(),
  interests: z.array(z.any()).optional(),
  memberBehaviors: z.array(z.any()).optional(),
});

export const linkedinCampaignSchema = linkedinBaseSchema.superRefine(
  (data, ctx) => {
    // Validate adAccountID (required)
    if (!data.adAccountID) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["adAccountID"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    // Validate budget (required and must be a valid positive number)
    const numericBudget = Number(data.budget);
    if (isNaN(numericBudget) || !numericBudget) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["budget"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    // Validate conversionActions (required when objective is website conversions)
    if (data.objective === ADS_CAMPAIGN_OBJECTIVE_WEBSITE_CONVERSIONS_OPTION) {
      if (
        !data.conversionActions ||
        !Array.isArray(data.conversionActions) ||
        data.conversionActions.length === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["conversionActions"],
          message: i18next.t(FIELD_REQUIRED),
        });
      }
    }

    if (data.budget && numericBudget < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["budget"],
        message: i18next.t(FIELD_NUMBER_NEGATIVE),
      });
    }

    // Validate typeBudget (required)
    if (!data.typeBudget) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["typeBudget"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    // Validate adFormat (required)
    if (!data.adFormat) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["adFormat"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    // Validate startDate (always required)
    const startDate = data.startDate ? new Date(data.startDate) : null;
    if (!startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["startDate"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    // Validate endDate (required if lifetime budget)
    const endDate = data.endDate ? new Date(data.endDate) : null;
    const isLifetime =
      data.typeBudget === ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION;

    if (isLifetime && !endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endDate"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    // Validate dates relationship
    if (startDate && endDate) {
      const differenceDays = differenceInDays(endDate, startDate);

      if (differenceDays <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["startDate"],
          message: i18next.t(FIELD_CAMPAIGN_MUST_HAVE_ONE_DAY_ACTIVE),
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["endDate"],
          message: i18next.t(FIELD_CAMPAIGN_MUST_HAVE_ONE_DAY_ACTIVE),
        });
      }

      if (!isAfter(new Date(endDate), new Date(startDate))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["startDate"],
          message: i18next.t(START_DATE_AND_END_DATE_VALIDATION),
        });
      }
    }

    if (endDate) {
      const now = new Date();
      if (endDate.getTime() < now.getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["endDate"],
          message: i18next.t(FIELD_END_DATE_LESS_THAN_NOW),
        });
      }
    }

    // Validate locations (required, minimum 1 element)
    if (!data.locations || data.locations.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["locations"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    // Validate languages (required)
    if (!data.languages) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["languages"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    // Validate ageRange (required, must be array with 2 numbers [min, max])
    if (!data.ageRange || !Array.isArray(data.ageRange)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ageRange"],
        message: i18next.t(FIELD_REQUIRED),
      });
    } else {
      if (data.ageRange.length !== 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["ageRange"],
          message: i18next.t(FIELD_REQUIRED),
        });
      } else {
        const [min, max] = data.ageRange;
        if (
          typeof min !== "number" ||
          typeof max !== "number" ||
          min >= max ||
          min < 18 ||
          max > 65
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["ageRange"],
            message: i18next.t(FIELD_REQUIRED),
          });
        }
      }
    }

    // Validate genders (required, must have at least one value true)
    if (!data.genders || typeof data.genders !== "object") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["genders"],
        message: i18next.t(FIELD_REQUIRED),
      });
    } else {
      const genderValues = Object.values(data.genders);
      if (!genderValues.includes(true)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["genders"],
          message: i18next.t(FIELD_REQUIRED),
        });
      }
    }
  }
);
